import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Fingerprint, Loader2, RefreshCw } from "lucide-react";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { privateAiSettings, setPrivateAi } from "../lib/api";
import { ensureIndex, syncIndex, clearIndex, indexStats } from "../lib/localIndex";
import { tr } from "../lib/i18n";

export const usePrivateMode = () => {
  const { data } = useQuery({ queryKey: ["private-ai"], queryFn: privateAiSettings, staleTime: 60000, retry: false });
  const on = Boolean(data?.enabled);
  useEffect(() => { if (on) ensureIndex().catch(() => {}); }, [on]);
  return on;
};

const Row = ({ Icon, title, children, testId }) => (
  <div className="mt-6" data-testid={testId}>
    <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5 mb-2"><Icon className="h-3.5 w-3.5" /> {title}</h4>
    <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-sm space-y-2">{children}</div>
  </div>
);

// Security sheet: Private mode — on-device search index; Ask sends only device-picked snippets; chat history stays in the browser.
export const PrivateModeRow = ({ isOwner }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["private-ai"], queryFn: privateAiSettings, retry: false });
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(indexStats());
  useEffect(() => { if (data?.enabled) ensureIndex().then(() => setStats(indexStats())).catch(() => {}); }, [data?.enabled]);
  if (!data) return null;
  const flip = async (v) => {
    setBusy(true);
    try {
      qc.setQueryData(["private-ai"], await setPrivateAi(v));
      if (v) { await syncIndex(true); setStats(indexStats()); toast.success(tr("Private mode on — your device now does the searching")); }
      else { await clearIndex(); setStats(indexStats()); toast.success(tr("Private mode off — back to server search")); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const resync = async () => { setBusy(true); try { await syncIndex(true); setStats(indexStats()); toast.success(tr("Index refreshed")); } catch { toast.error(tr("Could not refresh")); } finally { setBusy(false); } };
  return (
    <Row Icon={Fingerprint} title={tr("Private mode")} testId="private-mode-section">
      <div className="flex items-center justify-between gap-3">
        <div><div className="font-medium">{tr("Search and ask on this device")}</div><div className="text-xs text-muted-foreground" data-testid="private-mode-status">{tr("Your browser keeps its own index of the vault. Searches never leave the device, and “Ask your vault” sends the model only the few passages your device picked — not the question against the whole vault. Chat history stays in this browser.")}</div></div>
        <Switch data-testid="private-mode-toggle" checked={data.enabled} disabled={busy || !isOwner} onCheckedChange={flip} />
      </div>
      {data.enabled && (
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground border-t border-border pt-2">
          <span data-testid="private-index-stats">{stats.docs} {tr("documents indexed on this device")}{stats.lastSync ? ` · ${new Date(stats.lastSync).toLocaleTimeString()}` : ""}</span>
          <Button size="sm" variant="ghost" className="h-7 rounded-full gap-1" disabled={busy} onClick={resync} data-testid="private-index-refresh">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} {tr("Refresh")}</Button>
        </div>
      )}
    </Row>
  );
};
